<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import type { ProductCategoryProps } from "./ProductCategoryProps.js";
	import ProductCard from "../components/Product/ProductCard.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ProductCategoryFilters from "../components/ProductCategoryFilters.svelte";
	import Branches from "../components/Branches.svelte";
	import WeddingsBanner from "../components/Wedding/WeddingsBanner.svelte";
	import Content from "../components/Content.svelte";
	import { onMount, setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { PriorityLabel } from "../../../core/schema/PriorityLabel.js";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import { getClients } from "../appsync/getClients.js";
	import Preloader from "../components/Preloader.svelte";
	import { inflectFillingsLeft } from "../../../core/schema/inflectFillingsLeft.js";
	import { canBuyFillings } from "../../../core/schema/utils/canBuyFillings.js";
	import { getCartStore } from "../contexts/cartStoreContextKey.js";
	import { isCartEmpty } from "../stores/cart/isCartEmpty";
	import CartSummary from "../components/Cart/CartSummary.svelte";
	import HowItWorks from "../components/HowItWorks.svelte";
	import { christmasSteps } from "../data/christmasSteps";
	import { valentinesSteps } from "../data/valentinesSteps";
	import { christmasFaq } from "../data/christmasFaqs";
	import FrequentlyAskedQuestions from "../components/FrequentlyAskedQuestions.svelte";
	import { getImagesForProduct } from "../../../core/schema/utils/getImagesForProduct.js";
	import { getDefaultSizeForProductForCustomer } from "../../../core/schema/utils/getDefaultSizeForProductForCustomer.js";
	import { isEnum } from "../../../core/utils/enum/isEnum";
	import { ProductOption } from "../../../core/schema/ProductOption";
	import Message from "../components/Message.svelte";
	import CustomCakeBanner from "../components/CustomCake/CustomCakeBanner.svelte";
	import type { Inquiry } from "../../../core/schema/Inquiry.js";
	import getWeddingInquiryQuery from "../appsync/queries/getWeddingInquiry.graphql";
	import { isInquiryOfType } from "../../../core/schema/utils/isInquiryOfType.js";
	import { InquiryType } from "../../../core/schema/InquiryType.js";

	type $$Props = ProductCategoryProps;

	const cartStore = getCartStore();
	const { products, category, seoPageType, filtrations, maxProductsPerCategory, requiredCategories, title, isExpress } =
		$$restProps as $$Props;
	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE, STORAGE_URL } = globalSettings;

	let activeFilter = seoPageType;

	if (isExpress) {
		activeFilter = "express";
	}

	const [thumbnailProduct] = products.toSorted((first, second) => {
		if (first.priorityLabel === PriorityLabel.Favorite) {
			return first.priorityLabel === second.priorityLabel ? 0 : -1;
		}
		return 0;
	});

	if (thumbnailProduct) {
		setContext(
			thumbnailImageContextKey,
			`${STORAGE_URL}/${getImagesForProduct(thumbnailProduct, getDefaultSizeForProductForCustomer(thumbnailProduct))[0]?.path}`,
		);
	}

	$: filteredProducts = products.filter(({ categoryIds, filtrationLabels, priorityLabel, enabledOptions }) => {
		if (!activeFilter || isExpress) {
			return true;
		}

		return (
			(categoryIds.includes(category.id) && filtrationLabels[TENANT_LOCALE].includes(activeFilter)) ||
			priorityLabel?.toString() === activeFilter ||
			(isEnum(ProductOption)(activeFilter) && enabledOptions.includes(activeFilter))
		);
	});

	const getWeddingInquiry = createPromiseStore(async (id: Inquiry["id"]) => {
		const { graphQL } = await getClients(globalSettings);
		const weddingInquiry = await graphQL.query<Inquiry, { id: Inquiry["id"] }>({
			query: getWeddingInquiryQuery,
			variables: {
				id,
			},
		});

		const canBuy =
			weddingInquiry && isInquiryOfType(weddingInquiry, InquiryType.Wedding) && canBuyFillings(weddingInquiry);
		if (!canBuy) {
			return false;
		}

		localStorage.setItem("weddingInquiryId", weddingInquiry.id);

		return true;
	});

	onMount(async () => {
		const urlParams = new URLSearchParams(window.location.search);
		const filterValue = urlParams.get("filtr");
		if (filterValue) {
			activeFilter = filterValue;
		}

		if (category.id === requiredCategories.fillings.id) {
			const weddingInquiryId = urlParams.get("id");

			if (!weddingInquiryId) {
				window.location.href = "/";
			} else {
				const response = await getWeddingInquiry.call(weddingInquiryId);
				if (!response) {
					window.location.href = "/";
				}
			}
		}
	});

	$: productsOfCategoryInCart = $cartStore.value.order.products.reduce((acc, product) => {
		const productDetail = $cartStore.value.order.products.find(({ id }) => product.id === id);
		if (!productDetail?.product.categoryIds.includes(category.id)) {
			return acc;
		}
		return acc + product.quantity;
	}, 0);
	$: hasItemsInCart = !isCartEmpty($cartStore.value);
	$: enabledOption = isEnum(ProductOption)(seoPageType) ? seoPageType : undefined;
</script>

<Layout hasPanel={hasItemsInCart && category.id !== requiredCategories.fillings.id}>
	<div class="container">
		{#if category.id === requiredCategories.fillings.id && ($getWeddingInquiry.status !== "success" || !$getWeddingInquiry.result)}
			<Preloader />
		{:else}
			<section>
				<h1
					class="mb-8 mt-5 p-0 text-center text-[2rem] font-semibold leading-10 lg:mb-[1.625rem] lg:mt-0 lg:text-xl lg:leading-[3.25rem]"
				>
					{nbspify(title)}
				</h1>
				<div class="mx-auto my-4 max-w-[43.75rem] text-center text-base">
					<Content content={category.description[TENANT_LOCALE]} />
				</div>
				{#if filtrations.length > 0}
					<ProductCategoryFilters filtration={filtrations} bind:active={activeFilter} {category} />
				{/if}

				<div class="product-card-wrapper">
					{#each filteredProducts as product, index (product.id)}
						<ProductCard {enabledOption} {product} {index} {maxProductsPerCategory} isWarehouseProduct={isExpress} />
					{/each}

					{#if isExpress && filteredProducts.length === 0}
						<Message variant="express" class="max-w-lg text-center">
							<p>Omlouváme se, ale již nemáme žádný dort k expresnímu objednání.</p>
							<p>Dorty na zítra lze objednat pouze do vyprodání předchystané várky.</p>
						</Message>
					{/if}
				</div>

				{#if category.id === requiredCategories.fillings.id && maxProductsPerCategory[category.id] !== undefined}
					{@const maxFillings = Number(maxProductsPerCategory[category.id])}
					{@const fillings = productsOfCategoryInCart}
					<div
						class="pointer-events-none fixed bottom-0 left-0 z-40 mb-9 w-full text-center sm:left-auto sm:right-[50%] sm:translate-x-[50%] md:mb-6 md:w-fit"
					>
						<div
							class="pointer-events-auto mx-3 hidden h-[calc(100%-2.25rem)] flex-col items-center justify-center gap-2 rounded-full bg-white px-16 py-4 text-xs font-semibold shadow-[0_0_15px_rgba(0,0,0,0.2)] sm:flex sm:flex-row sm:gap-12 sm:text-sm md:text-base lg:gap-16 xl:gap-20"
						>
							{#if maxFillings - fillings > 0}
								<p>
									Můžeš si vybrat až {maxFillings}
									{inflectFillingsLeft(maxFillings)}. Máš vybrané {fillings} z {maxFillings}
								</p>
							{:else}
								<p>Do košíku již nelze přidat žádné další náplně</p>
							{/if}
						</div>
						<div
							class="pointer-events-auto mx-3 flex h-[calc(100%-2.25rem)] flex-col items-center justify-center gap-2 rounded-full bg-white px-16 py-4 text-xs font-semibold shadow-[0_0_15px_rgba(0,0,0,0.2)] sm:hidden sm:flex-row sm:gap-12 sm:text-sm md:text-base lg:gap-16 xl:gap-20"
						>
							{#if maxFillings - fillings > 0}
								<p>
									Máš vybrané {fillings} z {maxFillings}
									{inflectFillingsLeft(maxFillings)}
								</p>
							{:else}
								<p>Do košíku již nelze přidat žádné další náplně</p>
							{/if}
						</div>
					</div>
				{/if}
			</section>
		{/if}

		{#if category.id !== requiredCategories.fillings.id}
			<CartSummary linkToCart buttonText="Pokračovat v objednávce" />
		{/if}

		<section>
			{#if category.id === requiredCategories.christmas.id}
				<HowItWorks steps={christmasSteps} />
			{:else if category.id === requiredCategories.valentines.id}
				<HowItWorks steps={valentinesSteps} />
			{:else if category.id === requiredCategories.cakes.id}
				<CustomCakeBanner buttonText="Chci dort na míru" />
			{:else if !isExpress}
				<WeddingsBanner />
			{/if}
		</section>

		{#if category.id === requiredCategories.christmas.id}
			<div class="container">
				<div class="mt-6 flex flex-col items-center gap-8 md:mt-10">
					<span class="md:text-md text-center text-base font-semibold uppercase leading-7 tracking-[0.9375rem]">
						Máte otázku?
					</span>
					<h1
						class="m-0 p-0 text-center text-[1.8rem] font-semibold leading-10 md:text-2xl lg:mt-0 lg:leading-[4.25rem]"
					>
						{nbspify("Odpovědi na časté dotazy")}
					</h1>
				</div>

				<FrequentlyAskedQuestions questionAndAnswers={christmasFaq} />
			</div>
		{/if}

		<section>
			<Branches />
		</section>
	</div>
</Layout>
